import { Button } from '@consigli/facade';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FiChevronDown, FiChevronRight, FiRepeat, FiTrash2 } from 'react-icons/fi';

export interface MeetingSeriesCardProps {
  title: string;
  meetingCount: number;
  nextMeetingLabel?: string;
  lastMeetingLabel?: string;
  isExpanded: boolean;
  /** At least one past meeting in the series is still waiting for its transcript. */
  hasPendingTranscripts?: boolean;
  onExpandToggle: () => void;
  onDeleteSeries: () => void;
}

export const MeetingSeriesCard = ({
  title,
  meetingCount,
  nextMeetingLabel,
  lastMeetingLabel,
  isExpanded,
  hasPendingTranscripts,
  onExpandToggle,
  onDeleteSeries,
}: MeetingSeriesCardProps) => {
  const { t } = useTranslation();

  const handleDeleteClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDeleteSeries();
    },
    [onDeleteSeries],
  );

  return (
    <button
      type="button"
      onClick={onExpandToggle}
      className="group w-full p-6 hover:bg-gray-50 transition-colors duration-200 text-left"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <FiRepeat size={18} className="text-blue-600 flex-shrink-0" />
            <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
            <span className="text-sm text-gray-500 font-medium">
              ({meetingCount}{' '}
              {meetingCount === 1
                ? t('meeting-secretary.meetings.meeting')
                : t('meeting-secretary.meetings.meetings')}
              )
            </span>
            {hasPendingTranscripts && (
              <span
                className="w-2 h-2 rounded-full bg-amber-600 flex-shrink-0"
                title={t('meeting-secretary.meetings.transcript-not-run-series-hint')}
                aria-label={t('meeting-secretary.meetings.transcript-not-run-series-hint')}
              />
            )}
          </div>
          <div className="text-sm text-gray-600 space-y-1">
            {nextMeetingLabel && (
              <p>
                <span className="font-medium">{t('meeting-secretary.meetings.next-meeting')}:</span>{' '}
                {nextMeetingLabel}
              </p>
            )}
            {lastMeetingLabel && !nextMeetingLabel && (
              <p>
                <span className="font-medium">{t('meeting-secretary.meetings.last-meeting')}:</span>{' '}
                {lastMeetingLabel}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-col items-center gap-1 flex-shrink-0 pt-1">
          <Button
            iconOnly
            icon={FiTrash2}
            tertiary
            rounded
            onClick={handleDeleteClick}
            title={t('meeting-secretary.meetings.delete-series')}
            aria-label={t('meeting-secretary.meetings.delete-series')}
            iconSize={18}
            className="text-gray-400 hover:text-gray-600 opacity-0 transition-opacity duration-150 group-hover:opacity-100 focus:opacity-100 focus-visible:opacity-100 max-md:opacity-100"
          />
          {isExpanded ? (
            <FiChevronDown size={20} className="text-gray-600" />
          ) : (
            <FiChevronRight size={20} className="text-gray-600" />
          )}
        </div>
      </div>
    </button>
  );
};
